import { Injectable } from '@angular/core';
import { eBlockchains, eChainId, ePlatforms } from '@common/networks';
import { AuthService } from '@services/auth/auth.service';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { BlockchainApi, IBlockchain } from '../api/blockchain';
import { IToken, TokenApi } from '../api/token';
import { EventTransportService } from '../event-transport/event-transport.service';
import { StorageService } from '../storage/storage.service';
import { getPlatformFromString } from '../wallets/wallet.service';

export interface IGasLessTxInfo
{
    id: number;
    paymaster: string;
    supports: boolean;
}

export enum eEthereumNetworks
{
    mainnet = 'mainnet',
    kovan = 'kovan',
    ropsten = 'ropsten',
}

export interface IBlockchainNetwork extends IBlockchain
{
    network: boolean;
}

export const BscNetworks = {
    mainnet: { chainId: 56 },
    testnet: { chainId: 97 },
};

export const PolygonNetworks = {
    mainnet: { chainId: 137 },
    testnet: { chainId: 80002 },
};

export const PulseChainNetworks = {
    // mainnet: {chainId: null},
    testnet: { chainId: 943 },
};

export type NetworkStat = {
    [ key: number ]: boolean;
};
export type Platform = 'ethereum' | 'bsc' | 'polygon' | 'pulsechain';

export type Networks = keyof typeof eEthereumNetworks | keyof typeof BscNetworks | keyof typeof PolygonNetworks | keyof typeof PulseChainNetworks | string | number;

@Injectable({
    providedIn: 'root'
})
export class NetworksService
{

    private blockchain: IBlockchain[];
    private coins: IToken[];
    private network: NetworkStat;
    private networkLoaded: ReplaySubject<boolean> = new ReplaySubject(1);
    private coinsLoaded: ReplaySubject<boolean> = new ReplaySubject(1);

    private initialized: boolean = false;

    constructor(
        private storage: StorageService,
        private eventTransport: EventTransportService,
        private tokenApi: TokenApi,
        public authService: AuthService,
        private blockchainApi: BlockchainApi
    )
    {
        this.authService.loginState.subscribe(state =>
        {
            if (!this.initialized && state)
            {
                this.initialized = true;
            }
            this.init();
        });
    }

    private async init()
    {
        this.blockchain = await this.blockchainApi.getAll();

        const blockchainId = this.blockchain.map(v => v.id);

        this.loadCoins(blockchainId);

        let storageNetworks = await this.storage.get('networksChain');
        let customTokens = await this.storage.get('customTokens');

        if (!customTokens)
        {
            customTokens = [
                {
                    chainId: 56,
                    address: '0x84063552B298AcAc5324b0ebD8B71dfa8a8c67C6'
                },
                {
                    chainId: 56,
                    address: '0x3cb7200c9a678b1212e27b286b8daebe335c35b8'
                }
            ];
            await this.storage.set('customTokens', customTokens);
        }

        if (!storageNetworks)
        {
            storageNetworks = await this.migration();
            if (storageNetworks)
            {
                await this.storage.set('networksChain', storageNetworks);
            }
        }

        if (!storageNetworks)
        {
            await this.storage.set('networksChain', environment.networksBoolean);
            this.network = environment.networksBoolean;
        }
        else
        {
            this.network = storageNetworks;
        }

        this.networkLoaded.next(true);
    }

    private async loadCoins(_blockchainId: number[])
    {
        if (await this.authService.currentLoginState())
        {
            this.coins = await this.tokenApi.getCoinByBlockchainId(_blockchainId);
        }

        this.coinsLoaded.next(true);
    }

    public async setNetworkByChainId(_chainId: number, _network: boolean)
    {
        const storageNetworks = await this.storage.get('networksChain') || {};

        this.network[ _chainId ] = _network;

        storageNetworks[ _chainId ] = _network;

        this.storage.set('networksChain', storageNetworks);

        this.eventTransport.send('BLOCKCHAIN_CHANGE');
    }

    public async getNetworkByChainId(_chainId: number)
    {
        if (!this.network || !this.network[ _chainId ])
        {
            const storageNetworks = await this.storage.get('networksChain') || {};

            if (!storageNetworks[ _chainId ])
            {
                await this.setNetworkByChainId(_chainId, environment.networks[ _chainId ]);
            } else
            {
                this.network[ _chainId ] = storageNetworks[ _chainId ];
            }
        }

        return this.network[ _chainId ];
    }

    public async getMyNetwork()
    {
        await this.networkLoaded.pipe(take(1)).toPromise();

        let myNetwork = [];

        for (let key in this.network)
        {
            if (this.network[ key ])
            {
                myNetwork.push(Number(key));
            }
        }

        return myNetwork;
    }

    public async getCoinByChainId(_chainId: number)
    {
        await this.networkLoaded.pipe(take(1)).toPromise();
        await this.coinsLoaded.pipe(take(1)).toPromise();

        return this.coins.find(v => v.blockchainMain.chain_id === _chainId);
    }

    public async getCoinByPlatform(_platform: string)
    {
        await this.networkLoaded.pipe(take(1)).toPromise();
        await this.coinsLoaded.pipe(take(1)).toPromise();

        const block = this.blockchain.filter(v => v.platformMain.name === _platform).map(v => v.id);
        return this.coins.filter(v => block.includes(v.blockchain));
    }

    public async getBlockchainByChainId(_chainId: number)
    {
        await this.networkLoaded.pipe(take(1)).toPromise();

        return this.blockchain.find(v =>
        {
            return v.chain_id === +_chainId;
        });
    }

    public async getMainnetByChainId(_chainId: number)
    {
        await this.networkLoaded.pipe(take(1)).toPromise();
        const currentChain = await this.getBlockchainByChainId(_chainId);
        const mainnet = this.blockchain.find(v =>
            v.name === currentChain.name &&
            !v.testnet ||
            (v.chain_id === 1 && (currentChain.chain_id === 5 || currentChain.chain_id === eChainId.ETHEREUM_SEPOLIA)));
        if (mainnet)
            return mainnet;
        return currentChain;
    }

    public async getTokenByChainIdAndAddress(_chainId: number, _address: string = null)
    {
        await this.networkLoaded.pipe(take(1)).toPromise();

        if (_address === null)
        {
            return await this.getCoinByChainId(_chainId);
        }
        const blockchain = this.blockchain.find(v => v.chain_id === _chainId);

        let token: any = await this.tokenApi.getTokenByBlockchainIdAndAddress(blockchain.id, _address);

        if (!token)
        {
            token = {
                symbol: 'WLC',
                id: null,
                address: _address,
                blockchainMain: blockchain,
            };
        }
        return token;
    }

    public async gasLessSupportByTokenId(id: number): Promise<IGasLessTxInfo>
    {
        const res = await this.tokenApi.supportsGasLess(id);
        return res;
    }

    public async getBlockchains(_platform: string = null, _isReturnBlockchainId: boolean = false, _isReturnDBDataBlockchain: boolean = false)
    {
        await this.networkLoaded.pipe(take(1)).toPromise();

        let blockAndNetwork = this.blockchain.map((v: any) =>
        {
            v.network = this.network[ v.chain_id ];
            return v;
        });

        if (_platform != null)
        {
            blockAndNetwork = blockAndNetwork.filter(v => v.platformMain.name === _platform);
        }

        if (_isReturnDBDataBlockchain)
        {
            blockAndNetwork = blockAndNetwork.filter(v => v.network);
        }
        else if (_isReturnBlockchainId)
        {
            blockAndNetwork = blockAndNetwork.filter(v => v.network).map(v => v.id);
        }

        return blockAndNetwork;
    }

    public getPlatformByChainId(_chainId: number): ePlatforms
    {
        const blockchain = this.blockchain.find(v => v.chain_id == _chainId);

        if (!blockchain)
        {
            return null;
        }

        return getPlatformFromString(blockchain.platformMain.name);
    }

    public getBlockchainNameByChainId(_chainId: number): eBlockchains
    {
        switch (_chainId)
        {
            case 1:
                return eBlockchains.ETHEREUM;
            case 3:
                return eBlockchains.ROPSTEN;
            case 42:
                return eBlockchains.KOVAN;
            case 56:
            case 97:
                return eBlockchains.BSC;
            case 137:
            case 80002:
                return eBlockchains.POLYGON;
            case 943:
                return eBlockchains.PULSECHAIN;
        }
    }

    //network migration, default mainnet = true
    private async migration()
    {
        const storageNetworks = await this.storage.get('networks');

        if (!storageNetworks)
        {
            return null;
        }

        let network = {
            1: true,//ethereum
            5: false,//goerli
            3: false,//ropsten
            42: false,//kovan
            56: true,//bsc_main
            97: false,//bsc_test
            137: true,//polygon_main
            80002: false,//polygon_test
            943: false,//pulseChain_test
        };
        for (let blockchain in storageNetworks)
        {
            let chainId = +this.getChainIdByNetwork(blockchain, storageNetworks[ blockchain ]);

            if (!!chainId)
            {
                network[ chainId ] = true;
            }
        }

        return network;
    }

    private getChainIdByNetwork(_blockchain: string, _network: string): number
    {
        switch (_blockchain)
        {
            case 'ethereum':
                if (_network === 'mainnet')
                {
                    return 1;
                }
                else if (_network === 'kovan')
                {
                    return 42;
                }
                else if (_network === 'ropsten')
                {
                    return 3;
                }
                else if (_network === 'goerli')
                {
                    return 5;
                }
                break;
            case 'bsc':
                if (_network === 'mainnet')
                {
                    return 56;
                }
                else if (_network === 'testnet')
                {
                    return 97;
                }
                break;
            case 'polygon':
                if (_network === 'mainnet')
                {
                    return 137;
                }
                else if (_network === 'testnet')
                {
                    return 80002;
                }
                break;
            case 'pulsechain':
                if (_network === 'testnet')
                {
                    return 943;
                }
                break;
        }

        return null;
    }
}
